import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useListTrafficConnectionsQuery, useListTrafficSourcesQuery, type TrafficConnection } from '../../api/operator'
import { Loading, QueryError } from '../../ui/QueryState'
import { OperatorHeader } from './common'
import { healthText } from '../../model/traffic'

/**
 * Where visitor numbers are collected from (docs/plan/site-traffic.md
 * §9.2).
 *
 * **Read only.** A platform connection is deployment configuration —
 * declared in the stack's context and checked at synth — so there is
 * nothing here to edit, and a form that looked as though it could add
 * one would be offering a change the platform cannot make. What the
 * operator does need from here is the one step that is theirs: the
 * secret a connection's token is pasted into, named by the platform so
 * the client never builds a secret's name itself.
 */
export function OperatorConnections() {
  const connections = useListTrafficConnectionsQuery()
  const sources = useListTrafficSourcesQuery()

  if (connections.isLoading) return <Loading label="Loading connections…" />
  if (connections.error || !connections.data) {
    return <QueryError error={connections.error} title="Could not load traffic connections" />
  }
  const declared = connections.data.connections
  const sourceLabel = (kind: string) => sources.data?.sources.find((entry) => entry.kind === kind)?.label ?? kind

  return (
    <Stack spacing={3}>
      <OperatorHeader
        title="Traffic connections"
        subtitle="Where each site’s visitor numbers are collected from. The deployment declares them; a site is connected to one on its tenant’s Traffic tab."
      />
      <Paper>
        {declared.length === 0 ? (
          <Typography sx={{ p: 2 }} color="text.secondary">
            No traffic connections are declared for this deployment.
          </Typography>
        ) : (
          <List disablePadding>
            {declared.map((connection) => (
              <ListItem key={connection.ref} divider aria-label={connection.label}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" component="span">
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {connection.label}
                      </Box>
                      <HealthChip connection={connection} />
                    </Stack>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={
                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                      <span>
                        {sourceLabel(connection.kind)} · {ownerText(connection.owner)}
                      </span>
                      <span>
                        {connection.checkedAt
                          ? `Checked ${new Date(connection.checkedAt).toLocaleString()}`
                          : 'Not checked yet. It is checked the next time visits are collected.'}
                      </span>
                      {connection.configured !== true && (
                        <span>
                          Paste its token into the secret{' '}
                          <Box component="code" sx={{ fontFamily: 'ui-monospace, monospace', color: 'text.primary' }}>
                            {connection.secretName}
                          </Box>
                          .
                        </span>
                      )}
                    </Stack>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Stack>
  )
}

function HealthChip({ connection }: { connection: TrafficConnection }) {
  const colour = connection.configured === undefined ? 'default' : connection.configured ? 'success' : 'warning'
  return <Chip size="small" variant="outlined" color={colour} label={healthText(connection)} />
}

/** Who a connection belongs to. Only the platform holds one in v1. */
function ownerText(owner: string): string {
  return owner === 'platform' ? 'Held by the platform' : `Held by ${owner}`
}
